import { FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Checkbox,
  FormControlLabel,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  VBMActionButtons,
  VBMCardTitle,
  VBMEditButton,
} from '..';
import { Iconify, Label } from '@/components';
import usePostBookingSessionUpdate from '@/features/bookings-management/hooks/usePostBookingSessionUpdate';
import { BookingSession } from '@/features/bookings-management/types';

type VBMLinenAmenitiesProps = {
  bookingSession?: BookingSession;
  editable?: boolean;
};
type LinenAmenitiesSessionDto = Partial<
  Pick<BookingSession, 'linen' | 'essential_amenities'>
>;

export default function VBMLinenAmenities({
  bookingSession,
  editable = false,
}: VBMLinenAmenitiesProps) {
  const [isEdit, setIsEdit] = useState(false);
  const [linen, setLinen] = useState(!!bookingSession?.linen);
  const [essentialAmenities, setEssentialAmenities] = useState(
    !!bookingSession?.essential_amenities
  );
  const [searchParams] = useSearchParams();
  const sessionID = searchParams.get('sessionID');

  const { sessionUpdateMutation } = usePostBookingSessionUpdate({
    onSuccessFn: () => setIsEdit(false),
    successText: 'Linen and amenities updated',
  });

  useEffect(() => {
    setLinen(!!bookingSession?.linen);
    setEssentialAmenities(!!bookingSession?.essential_amenities);
  }, [bookingSession?.essential_amenities, bookingSession?.linen]);

  if (!bookingSession) return null;

  const handleCancel = () => {
    setLinen(!!bookingSession.linen);
    setEssentialAmenities(!!bookingSession.essential_amenities);
    setIsEdit(false);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const bookingSessionDto: LinenAmenitiesSessionDto = {};

    if (linen !== bookingSession.linen) {
      bookingSessionDto.linen = linen;
    }

    if (essentialAmenities !== bookingSession.essential_amenities) {
      bookingSessionDto.essential_amenities = essentialAmenities;
    }

    if (Object.keys(bookingSessionDto).length === 0) {
      setIsEdit(false);
      return;
    }

    sessionUpdateMutation.mutate({
      bookingSessionId: Number(sessionID ?? bookingSession.id),
      bookingSessionDto,
    });
  };

  const renderSetting = ({
    enabled,
    icon,
    label,
  }: {
    enabled: boolean;
    icon: string;
    label: string;
  }) => (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Iconify
        icon={icon}
        title={label}
        width={24}
        sx={icon === 'openmoji:bed-linen' ? linenIconSx : undefined}
      />
      <Typography fontSize={16} sx={{ flexGrow: 1 }}>
        {label}
      </Typography>
      <Label color={enabled ? 'success' : 'default'}>
        {enabled ? 'Included' : 'Not included'}
      </Label>
    </Stack>
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ position: 'relative', py: 0.5 }}
    >
      {!isEdit && editable && (
        <VBMEditButton onClickButton={() => setIsEdit(true)} />
      )}
      <Stack spacing={1} sx={{ width: '100%' }}>
        <VBMCardTitle title="Linen and Amenities" />
        {isEdit ? (
          <form onSubmit={handleSubmit}>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={linen}
                    name="linen"
                    onChange={(event) => setLinen(event.target.checked)}
                  />
                }
                label="Linen"
                sx={{ ml: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={essentialAmenities}
                    name="essential_amenities"
                    onChange={(event) =>
                      setEssentialAmenities(event.target.checked)
                    }
                  />
                }
                label="Essential Amenities"
                sx={{ ml: 0 }}
              />
              <VBMActionButtons
                onClickCancel={handleCancel}
                isSubmitting={sessionUpdateMutation.isLoading}
              />
            </Stack>
          </form>
        ) : (
          <>
            {sessionUpdateMutation.isLoading ? (
              <Skeleton height={50} />
            ) : (
              <Stack spacing={1.5} sx={{ pl: 1, pt: 0.5 }}>
                {renderSetting({
                  enabled: !!bookingSession.linen,
                  icon: 'openmoji:bed-linen',
                  label: 'Linen',
                })}
                {renderSetting({
                  enabled: !!bookingSession.essential_amenities,
                  icon: 'fa-solid:pump-soap',
                  label: 'Essential Amenities',
                })}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
}

const linenIconSx = {
  color: 'info.main',
  '& [fill]:not([fill="none"])': {
    fill: 'currentColor !important',
  },
  '& [stroke]:not([stroke="none"])': {
    stroke: 'currentColor !important',
  },
} as const;
